import React, { useEffect, useMemo, useRef, useState } from "react";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import Boost from "highcharts/modules/boost";
import { formatDisplayDate } from "../../../utilities/CommonUtilities";
import { applyLocalNudgesOrderIndependent } from "../../../utilities/ChartUtilities";
import { useResolvedDark } from "../../../ThemeProvider/ThemeContext";

const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

const wrapHue = (hue) => ((hue % 360) + 360) % 360;

const getSignedHueOffset = (hue, centerHue) =>
  ((wrapHue(hue - centerHue) + 180) % 360) - 180;

const parseHslColor = (colorValue) => {
  if (typeof colorValue !== "string") {
    return null;
  }

  const match = colorValue.match(
    /hsla?\(\s*([0-9.]+)\s*,\s*([0-9.]+)%\s*,\s*([0-9.]+)%/i
  );

  if (!match) {
    return null;
  }

  return {
    hue: Number(match[1]),
    sat: Number(match[2]),
    light: Number(match[3]),
  };
};

const toHslaString = (hue, sat, light, alpha) =>
  `hsla(${wrapHue(hue).toFixed(1)}, ${clamp(sat, 0, 100).toFixed(0)}%, ${clamp(
    light,
    0,
    100
  ).toFixed(0)}%, ${alpha})`;

const getFallbackGlowPalette = (isDark) =>
  isDark
    ? {
        positive: {
          color: "hsla(172, 58%, 58%, 0.34)",
          fillColor: "hsla(172, 58%, 58%, 0.26)",
        },
        negative: {
          color: "hsla(12, 55%, 64%, 0.34)",
          fillColor: "hsla(12, 55%, 64%, 0.26)",
        },
      }
    : {
        positive: {
          color: "hsla(172, 50%, 54%, 0.32)",
          fillColor: "hsla(172, 50%, 54%, 0.25)",
        },
        negative: {
          color: "hsla(12, 52%, 60%, 0.32)",
          fillColor: "hsla(12, 52%, 60%, 0.25)",
        },
      };

const deriveGlowPaletteFromLineColor = (lineColor, isDark) => {
  const parsed = parseHslColor(lineColor);

  if (!parsed) {
    return getFallbackGlowPalette(isDark);
  }

  const hueInfluence = getSignedHueOffset(parsed.hue, 210);
  const positiveHue = 178 + hueInfluence * 0.12;
  const negativeHue = 8 + hueInfluence * 0.08;
  const positiveSat = clamp(54 + (parsed.sat - 68) * 0.22, 48, 66);
  const negativeSat = clamp(52 + (parsed.sat - 68) * 0.2, 46, 64);

  if (isDark) {
    return {
      positive: {
        color: toHslaString(
          positiveHue,
          positiveSat + 2,
          clamp(parsed.light + 8, 56, 66),
          0.36
        ),
        fillColor: toHslaString(
          positiveHue,
          positiveSat,
          clamp(parsed.light + 10, 58, 68),
          0.28
        ),
      },
      negative: {
        color: toHslaString(
          negativeHue,
          negativeSat + 2,
          clamp(parsed.light + 10, 58, 68),
          0.36
        ),
        fillColor: toHslaString(
          negativeHue,
          negativeSat,
          clamp(parsed.light + 12, 60, 70),
          0.28
        ),
      },
    };
  }

  return {
    positive: {
      color: toHslaString(
        positiveHue,
        positiveSat + 2,
        clamp(parsed.light + 4, 50, 60),
        0.34
      ),
      fillColor: toHslaString(
        positiveHue,
        positiveSat,
        clamp(parsed.light + 6, 52, 62),
        0.26
      ),
    },
    negative: {
      color: toHslaString(
        negativeHue,
        negativeSat + 2,
        clamp(parsed.light + 6, 54, 64),
        0.34
      ),
      fillColor: toHslaString(
        negativeHue,
        negativeSat,
        clamp(parsed.light + 8, 56, 66),
        0.26
      ),
    },
  };
};

const buildGlowDataWithZeroCrossings = (data, predicate) => {
  if (!Array.isArray(data) || data.length === 0) {
    return [];
  }

  const output = [];

  for (let index = 0; index < data.length; index += 1) {
    const currentPoint = data[index];
    const nextPoint = data[index + 1];
    const [x, y] = currentPoint;
    const currentVisible = predicate(y);

    output.push([x, currentVisible ? y : null]);

    if (!nextPoint) {
      continue;
    }

    const [nextX, nextY] = nextPoint;

    if (
      !Number.isFinite(y) ||
      !Number.isFinite(nextY) ||
      y === nextY ||
      y === 0 ||
      nextY === 0
    ) {
      continue;
    }

    const crossesZero = (y < 0 && nextY > 0) || (y > 0 && nextY < 0);

    if (!crossesZero) {
      continue;
    }

    const zeroRatio = (0 - y) / (nextY - y);
    const zeroX = x + (nextX - x) * zeroRatio;
    output.push([zeroX, 0]);
  }

  return output;
};

const buildGlowAreaSeries = (sourceSeries, suffix, color, fillColor, predicate) => {
  if (!sourceSeries?.id || !Array.isArray(sourceSeries?.data)) {
    return null;
  }

  const data = buildGlowDataWithZeroCrossings(sourceSeries.data, predicate);
  const hasVisiblePoints = data.some(([, y]) => y !== null);

  if (!hasVisiblePoints) {
    return null;
  }

  return {
    id: `${sourceSeries.id}-${suffix}`,
    linkedTo: sourceSeries.id,
    type: "area",
    data,
    keys: ["x", "y"],
    color,
    fillColor,
    fillOpacity: 1,
    lineWidth: 0,
    threshold: 0,
    marker: { enabled: false },
    enableMouseTracking: false,
    showInLegend: false,
    states: {
      hover: { enabled: false },
      inactive: { opacity: 1 },
    },
    zIndex: 0,
  };
};

const buildPriceOverlaySeries = (priceOverlay, color) => {
  if (!priceOverlay?.ticker || !Array.isArray(priceOverlay?.data)) {
    return null;
  }

  const data = priceOverlay.data
    .map((point) => {
      const timestamp = new Date(point?.timestamp ?? point?.[0]).getTime();
      const price = Number(point?.current_price ?? point?.price ?? point?.[1]);

      return Number.isFinite(timestamp) && Number.isFinite(price)
        ? [timestamp, price]
        : null;
    })
    .filter(Boolean);

  if (data.length === 0) {
    return null;
  }

  return {
    id: "product-dashboard-price-overlay",
    type: "line",
    name: `${priceOverlay.label || priceOverlay.ticker} Price`,
    data,
    yAxis: "price-overlay-axis",
    color,
    dashStyle: "ShortDash",
    lineWidth: 2,
    zIndex: 3,
    isPriceOverlay: true,
  };
};

const ChartComponent = ({
  dashboardName,
  dashboardChartData,
  legends,
  handlePointClick,
  isDark: isDarkProp,
  layoutVersion = 0,
  priceOverlay = null,
}) => {
  const isDark = useResolvedDark(isDarkProp);
  const [monthlyData, setMonthlyData] = useState(false);
  const [range, setRange] = useState({ min: -1, max: 1 });
  const [viewportWidth, setViewportWidth] = useState(() =>
    typeof window === "undefined" ? 1024 : window.innerWidth
  );
  const chartWrapperRef = useRef(null);
  const chartRef = useRef(null);
  const isMobile = viewportWidth < 768;

  useEffect(() => {
    if (typeof window === "undefined") {
      return undefined;
    }

    let frameId = null;

    const handleResize = () => {
      if (frameId !== null) {
        cancelAnimationFrame(frameId);
      }

      frameId = requestAnimationFrame(() => {
        setViewportWidth(window.innerWidth);
      });
    };

    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
      if (frameId !== null) {
        cancelAnimationFrame(frameId);
      }
    };
  }, []);

  useEffect(() => {
    if (dashboardName === "GEOPOLITICAL_INDIA_V1") {
      setMonthlyData(true);
    } else {
      setMonthlyData(false);
    }
  }, [dashboardName]);

  Boost(Highcharts);

  function groupByDateAndIsin(data) {
    return data.reduce((acc, [date, score, isin]) => {
      const dateObj = new Date(date);
      const month = dateObj.getMonth() + 1;
      const year = dateObj.getFullYear();
      const key = `${year}-${month.toString().padStart(2, "0")}`;

      if (!acc[key]) {
        acc[key] = { date, isin, score: 0 };
      }

      if (acc[key].isin === isin) {
        acc[key].score += score;
      }
      return acc;
    }, {});
  }




const processData = (data) => {
  if (!data || data.length === 0) return { processedSeries: [], max: 0, min: 0 };

  let processedSeries;

  if (monthlyData) {
    const updated = data.map((dataPoint) => {
      const mapped = dataPoint.data.map((entry) => [entry[0], entry[1], entry[2]]);
      return {
        name: dataPoint.name,
        country: dataPoint.country,
        isin: dataPoint.isin,
        data: groupByDateAndIsin(mapped),
      };
    });

    processedSeries = updated.map((series) => {
      const d = Object.values(series.data).map((x) => [new Date(x.date), x.score, x.isin]);
      return {
        ...series,
        name: series.name,
        data: d,
        keys: ["x", "y", "isin"],
      };
    });
  } else {
    processedSeries = data.map((series) => {
      const d = (series.data || []).map((x) => [
        new Date(x[0]),
        parseFloat(Number(x[1]).toFixed(3)),
        x[2],
      ]);
      return {
        ...series,
        name: series.name,
        data: d,
        keys: ["x", "y", "isin"],
      };
    });
  }

  const seriesKeys = processedSeries.map((s) => s.isin || `${s.country}_${s.name}`);


  // const colors = applyLocalNudges(seriesKeys, { minHueGap: 2, maxNudges: 100 });

  // 2) Also stable even if render order changes (recommended if you reorder legend/series):
  const colors = applyLocalNudgesOrderIndependent(seriesKeys, { minHueGap: 18, maxNudges: 6 });

  processedSeries = processedSeries.map((s, i) => ({
    ...s,
    id: `product-dashboard-series-${i}`,
    color: colors[i], // Highcharts will use this directly
  }));

  const allY = processedSeries.flatMap((s) => (s.data || []).map((d) => d[1]));
  const max = allY.length ? Math.max(...allY) : 0;
  const min = allY.length ? Math.min(...allY) : 0;

  return {
    processedSeries: processedSeries.sort((a, b) => a.name.localeCompare(b.name)),
    max,
    min,
  };
};

  const { processedSeries, max, min } = useMemo(
    () => processData(dashboardChartData),
    // Important: re-run when theme changes so series colors update
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [dashboardChartData, monthlyData, isDark]
  );

  useEffect(() => {
    const absMax = Math.max(Math.abs(max), Math.abs(min));
    if (absMax >= 0.75) setRange({ min: -1, max: 1 });
    else if (absMax >= 0.5) setRange({ min: -0.75, max: 0.75 });
    else if (absMax >= 0.25) setRange({ min: -0.5, max: 0.5 });
    else if (absMax >= 0.1) setRange({ min: -0.25, max: 0.25 });
    else setRange({ min: -0.1, max: 0.1 });
  }, [max, min]);

  const chartTheme = useMemo(() => {
    if (isDark) {
      return {
        background: "#12172A",
        grid: "rgba(35,43,74,0.65)", // #232B4A with opacity
        axis: "#A6B0CF",
        zeroLine: "rgba(198, 208, 226, 0.46)",
        title: "#E8ECF8",
        tooltipBg: "rgba(15,20,36,0.95)",
        tooltipText: "#E8ECF8",
        tooltipBorder: "rgba(35,43,74,0.65)",
        price: "#F87171",
      };
    }
    // Light theme follows the desk tokens (CoTrader.css / ProductDashboard.css).
    return {
      background: "#FFFFFF",
      grid: "#eef1f5",
      axis: "#6b7280",
      zeroLine: "rgba(152, 162, 179, 0.8)",
      title: "#111827",
      tooltipBg: "rgba(255,255,255,0.98)",
      tooltipText: "#111827",
      tooltipBorder: "#d5dce7",
      price: "#b42318",
    };
  }, [isDark]);

  const priceOverlaySeries = useMemo(
    () => buildPriceOverlaySeries(priceOverlay, chartTheme.price),
    [chartTheme.price, priceOverlay],
  );

  const chartSeries = useMemo(() => {
    const baseSeries = processedSeries;
    const singleVisibleSeries = baseSeries.length === 1 ? baseSeries[0] : null;

    if (!singleVisibleSeries) {
      return priceOverlaySeries ? [...baseSeries, priceOverlaySeries] : baseSeries;
    }

    const glowPalette = deriveGlowPaletteFromLineColor(
      singleVisibleSeries.color,
      isDark
    );

    const positiveGlowSeries = buildGlowAreaSeries(
      singleVisibleSeries,
      "positive-glow",
      glowPalette.positive.color,
      glowPalette.positive.fillColor,
      (value) => Number.isFinite(value) && value > 0
    );
    const negativeGlowSeries = buildGlowAreaSeries(
      singleVisibleSeries,
      "negative-glow",
      glowPalette.negative.color,
      glowPalette.negative.fillColor,
      (value) => Number.isFinite(value) && value < 0
    );

    const scoreSeries = [
      ...[positiveGlowSeries, negativeGlowSeries].filter(Boolean),
      ...baseSeries,
    ];

    return priceOverlaySeries ? [...scoreSeries, priceOverlaySeries] : scoreSeries;
  }, [isDark, priceOverlaySeries, processedSeries]);

  const options = useMemo(
    () => ({
      chart: {
        type: "line",
        zoomType: "x",
        backgroundColor: chartTheme.background,
        spacingTop: 12,
        spacingLeft: 12,
        spacingRight: 16,
        spacingBottom: 28,
        style: {
          fontFamily: "inherit",
        },
      },
      boost: {
        useGPUSummarys: true,
        usePreAllocated: true,
      },
      accessibility: { enabled: false },
      title: { text: null },
      xAxis: {
        type: "datetime",
        gridLineColor: chartTheme.grid,
        lineColor: chartTheme.grid,
        tickColor: chartTheme.grid,
        labels: {
          style: { color: chartTheme.axis },
          reserveSpace: true,
          y: 18,
        },
        dateTimeLabelFormats: { day: "%e %b %Y", month: "%b '%y", year: "%Y" },
      },
      yAxis: priceOverlaySeries
        ? [
            {
              id: "score-axis",
              title: { text: "Sentiment Score", style: { color: chartTheme.axis } },
              min: range.min,
              max: range.max,
              endOnTick: false,
              startOnTick: false,
              gridLineColor: chartTheme.grid,
              lineColor: chartTheme.grid,
              plotLines: [
                {
                  value: 0,
                  color: chartTheme.zeroLine,
                  width: 1.4,
                  zIndex: 1,
                },
              ],
              labels: {
                style: { color: chartTheme.axis },
                reserveSpace: true,
              },
            },
            {
              id: "price-overlay-axis",
              title: { text: "Price", style: { color: chartTheme.price } },
              opposite: true,
              gridLineWidth: 0,
              lineColor: chartTheme.grid,
              labels: {
                style: { color: chartTheme.price },
                reserveSpace: true,
              },
            },
          ]
        : {
            title: { text: "Sentiment Score", style: { color: chartTheme.axis } },
            min: range.min,
            max: range.max,
            endOnTick: false,
            startOnTick: false,
            gridLineColor: chartTheme.grid,
            lineColor: chartTheme.grid,
            plotLines: [
              {
                value: 0,
                color: chartTheme.zeroLine,
                width: 1.4,
                zIndex: 1,
              },
            ],
            labels: {
              style: { color: chartTheme.axis },
              reserveSpace: true,
            },
          },
      legend: legends
        ? {
            align: isMobile? "center" : "right",
            verticalAlign: isMobile? "bottom" : "middle",
            layout: isMobile? "horizontal" : "vertical",
            itemStyle: { color: chartTheme.axis },
            itemHoverStyle: { color: chartTheme.title },
          }
        : false,
      plotOptions: {
        series: {
          cursor: "pointer",
          marker: { enabled: false },
          lineWidth: 2,
          states: {
            hover: { lineWidthPlus: 1 },
            inactive: { opacity: 0.25 },
          },
          point: {
            events: {
              click: function () {
                if (!this.series.options.isPriceOverlay && handlePointClick) {
                  handlePointClick(this);
                }
              },
            },
          },
        },
      },
      credits: { enabled: false },
      tooltip: {
        backgroundColor: chartTheme.tooltipBg,
        borderColor: chartTheme.tooltipBorder,
        borderRadius: 8,
        style: { color: chartTheme.tooltipText },
        formatter: function () {
          const isPriceOverlay = this.series.userOptions.isPriceOverlay;
          return (
            "<b>" +
            this.series.name +
            "</b><br>" +
            "Date: " +
            formatDisplayDate(this.x) +
            "<br>" +
            (isPriceOverlay ? "Price: " : "Score: ") +
            (isPriceOverlay ? Highcharts.numberFormat(this.y, 4) : this.y)
          );
        },
      },
      series: chartSeries,
    }),
    [
      chartSeries,
      chartTheme,
      legends,
      range.min,
      range.max,
      handlePointClick,
      isMobile,
      priceOverlaySeries,
    ]
  );

  useEffect(() => {
    let frameId = null;
    let resizeObserver;

    const attachResizeHandling = () => {
      const chart = chartRef.current?.chart;
      const chartContainer = chartWrapperRef.current;

      if (!chart || !chartContainer) {
        frameId = requestAnimationFrame(attachResizeHandling);
        return;
      }

      const syncChartToContainer = () => {
        // A kept-alive tab hidden with display:none has no layout; resizing
        // then is wasted work and the observer fires again once it is shown.
        if (chartContainer.getClientRects().length === 0) return;
        if (frameId !== null) {
          cancelAnimationFrame(frameId);
        }

        frameId = requestAnimationFrame(() => {
          chart.setSize(
            chartContainer.clientWidth || null,
            chartContainer.clientHeight || null,
            false
          );
          chart.reflow();
        });
      };

      syncChartToContainer();

      if (typeof ResizeObserver === "undefined") {
        return;
      }

      resizeObserver = new ResizeObserver(() => {
        syncChartToContainer();
      });

      resizeObserver.observe(chartContainer);
    };

    attachResizeHandling();

    return () => {
      resizeObserver?.disconnect();
      if (frameId !== null) {
        cancelAnimationFrame(frameId);
      }
    };
  }, []);

  useEffect(() => {
    const chart = chartRef.current?.chart;
    if (!chart) {
      return;
    }

    const frameId = requestAnimationFrame(() => {
      chart.reflow();
    });

    return () => {
      cancelAnimationFrame(frameId);
    };
  }, [
    processedSeries,
    chartSeries,
    range.min,
    range.max,
    legends,
    isDark,
    dashboardName,
    layoutVersion,
  ]);

  useEffect(() => {
    const chart = chartRef.current?.chart;
    const chartContainer = chartWrapperRef.current;

    if (!chart || !chartContainer) {
      return undefined;
    }

    let frameId = null;
    const syncChartToContainer = () => {
      if (frameId !== null) {
        cancelAnimationFrame(frameId);
      }

      frameId = requestAnimationFrame(() => {
        chart.setSize(
          chartContainer.clientWidth,
          chartContainer.clientHeight,
          false
        );
        chart.reflow();
      });
    };

    syncChartToContainer();

    return () => {
      if (frameId !== null) {
        cancelAnimationFrame(frameId);
      }
    };
  }, [layoutVersion, viewportWidth]);

  return (
    <div
      ref={chartWrapperRef}
      className="w-100 h-100"
      style={{ minHeight: 0 }}
    >
      <HighchartsReact
        ref={chartRef}
        highcharts={Highcharts}
        options={options}
        containerProps={{ style: { height: "100%", width: "100%" } }}
      />
    </div>
  );
};

export default ChartComponent;
